/**
 * Carrier MCP — carrier_ask + carrier_ask_describe (v2.0 live routing).
 *
 * IMPLEMENTATION STATUS: LIVE (Phase 2)
 * carrier_ask now routes via Claude Haiku 4.5 on AWS Bedrock tool-use.
 * The router receives the full TOOL_REGISTRY as its tool catalog and returns
 * a single tool_use block identifying the best matching tool + extracted params.
 *
 * ACTIVATION:
 *   - Requires AWS_ACCESS_KEY_ID + AWS_SECRET_ACCESS_KEY in Doppler carrier/dev+stg+prd.
 *   - Set CARRIER_ASK_ENABLED=true in Doppler to activate per-env.
 *   - Without the credentials/flag, the CLI throws AskRouterNotConfiguredError.
 *     The hosted worker returns routing_pending; do not copy that here.
 *
 * DESIGN CHOICES:
 *   - Router model: Claude Haiku 4.5 on Bedrock (us.anthropic.claude-haiku-4-5-20251001-v1:0).
 *   - Auth: SigV4 via aws4fetch — no AWS SDK bundle (CF Worker compatible).
 *   - tool_choice: { type: "any" } — forces a tool_use response.
 *   - Ambiguous/no-match: sentinel "carrier_clarify" tool for Haiku to signal.
 *   - HARD_BLOCK_TOOLS: always return confirm_token; never auto-execute.
 *   - Confirm token TTL: 120s. Single-use (deleted on consume).
 *   - Intent hashing: SHA-256, hex. Not stored verbatim (PII guard).
 *   - Audit log: Analytics Engine, fire-and-forget. Extended schema for router.
 *   - Rate-limit (429): returns friendly retry hint, never crashes.
 *
 * SAFETY FLOORS (non-negotiable):
 *   - Resolved tool name validated against TOOL_REGISTRY — no hallucination.
 *   - HARD_BLOCK_TOOLS always require confirm_token round-trip.
 *   - No-match / ambiguous: structured response, never fabricated tool call.
 */

import {
  type AiAttribution,
  BedrockRateLimitError,
  estimateCostUsd,
  DEFAULT_MODEL_ID,
  invokeTool,
  type ToolCallResult,
  type TokenUsage,
  ZERO_USAGE,
} from "@carrier/ai";
import { z } from "zod";
import { buildRouterCatalog, ROUTER_RULES, type RouterTool } from "@carrier/ocs-client";
import { ALL_TOOL_SCOPES } from "./tool-registry.js";
import { SCHEMA_CARRIER_ASK, withSchemaMarker } from "./audit-schema.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { TOOL_SCOPES, DESTRUCTIVE_TOOLS, type ToolContext, wrapHandler } from "./tools.js";
import { BACKLOG_TOOL_SCOPES } from "./tools-backlog.js";

// ---------------------------------------------------------------------------
// Tool registry — complete list of all MCP tool names known at build time.
// Resolved tool names are validated against this set before returning to caller.
// ---------------------------------------------------------------------------
const TOOL_REGISTRY: ReadonlySet<string> = new Set([
  // v1 OCS tools (43)
  ...Object.keys(TOOL_SCOPES),
  // v1.1 backlog tools
  ...Object.keys(BACKLOG_TOOL_SCOPES),
  // intelligence composites (8)
  "diagnose_subscriber",
  "fleet_health",
  "detect_usage_anomalies",
  "optimize_package",
  "churn_risk",
  "audit_network_coverage",
  "marketing_intelligence",
  "high_cost_subscribers",
  // event ring buffer (1)
  "list_recent_ocs_events",
  // CAR-39: rate-limit governor (1)
  "rate_limit_status",
  // CAR-38: Relay LU country history (1)
  "subscriber_country_history",
  // bundle depletion events (1) — CAR-42 wave-2
  "subscriber_depletion_events",
  // MCP App tools (3)
  "fleet_health_app",
  "provision_esim_wizard",
  "balance_topup_form",
  // greenzone whitelist tools (3) — portal-only, executed via Kapture browser automation
  "greenzone_whitelist_add",
  "greenzone_whitelist_remove",
  "greenzone_whitelist_list",
  // router tools (self-reference)
  "carrier_ask",
  "carrier_ask_describe",
  "generate_storefront_logo",
]);

// Tools that MUST NEVER auto-execute from a natural-language intent.
// Always require confirm_token round-trip regardless of confidence.
const HARD_BLOCK_TOOLS: ReadonlySet<string> = new Set([
  "clean_all_packages",
  "delete_subscriber_package",
  "modify_account_balance",
  "modify_subscriber_status",
  "change_sim_status",
  "reset_subscriber_gz_counter",
]);

// ---------------------------------------------------------------------------
// Confirm token helpers
// ---------------------------------------------------------------------------
const CONFIRM_TOKEN_TTL_SECONDS = 120;
const CONFIRM_TOKEN_PREFIX = "carrier_ask_confirm:";

function generateConfirmToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return bytesToHex(bytes);
}

async function storeConfirmToken(
  kv: KVNamespace,
  token: string,
  payload: Record<string, unknown>,
): Promise<void> {
  await kv.put(
    `${CONFIRM_TOKEN_PREFIX}${token}`,
    JSON.stringify(payload),
    { expirationTtl: CONFIRM_TOKEN_TTL_SECONDS },
  );
}

async function consumeConfirmToken(
  kv: KVNamespace,
  token: string,
): Promise<Record<string, unknown> | null> {
  const raw = await kv.get(`${CONFIRM_TOKEN_PREFIX}${token}`, "text");
  if (!raw) return null;
  await kv.delete(`${CONFIRM_TOKEN_PREFIX}${token}`);
  return JSON.parse(raw) as Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Intent hashing — SHA-256, hex-encoded.
// ---------------------------------------------------------------------------
export function hashIntent(intent: string): string {
  return bytesToHex(sha256(new TextEncoder().encode(intent)));
}

// ---------------------------------------------------------------------------
// Routing result shape
// ---------------------------------------------------------------------------
type RouteResult =
  | {
      match: "confirmed";
      resolved_tool: string;
      resolved_params: Record<string, unknown>;
      confidence: number;
      confirm_required: false;
      execution_note: string;
    }
  | {
      match: "pending_confirm";
      resolved_tool: string;
      resolved_params: Record<string, unknown>;
      confidence: number;
      confirm_required: true;
      confirm_token: string;
      confirm_expires_in_seconds: number;
      dry_run_preview?: string;
      safety_note: string;
    }
  | {
      match: "ambiguous";
      candidates: Array<{ tool: string; reason: string }>;
      clarifying_question: string;
    }
  | {
      match: "none";
      closest: Array<{ tool: string; reason: string }>;
      suggestion: string;
    }
  | {
      match: "routing_pending";
      intent_received: string;
      note: string;
      scaffold_version: string;
    }
  | {
      match: "rate_limited";
      retry_after_seconds: number;
      suggestion: string;
    };

// ---------------------------------------------------------------------------
// Bedrock request/response types (Bedrock converse-compatible, tool-use subset)
// ---------------------------------------------------------------------------
interface BedrockTool {
  name: string;
  description: string;
  input_schema: {
    type: "object";
    properties: Record<string, unknown>;
    required?: string[];
  };
}

// The Bedrock wire types that used to live here (BedrockPayload,
// BedrockResponse and its content blocks) went with the hand-rolled client.
// @carrier/ai owns the payload shape and returns a parsed ToolCallResult, so
// re-declaring them here would just be a second definition free to drift.
// BedrockTool above stays: the curated catalog entries need a concrete
// input_schema, and that shape is local to this file.

// ---------------------------------------------------------------------------
// Router tool catalog for Bedrock.
// Sentinel "carrier_clarify" signals ambiguity without hallucinating a match.
// ---------------------------------------------------------------------------
/**
 * Hand-written entries. These are now a FALLBACK: each tool's own registered
 * description wins where one exists (see @carrier/ocs-client router-catalog).
 * Their input_schema hints are still used — they tell the router which params
 * are worth extracting from an intent.
 */
const CURATED_ROUTER_TOOLS: BedrockTool[] = [
  {
    name: "carrier_clarify",
    description:
      "Use ONLY when the intent is genuinely ambiguous and you cannot determine a single best-matching tool. " +
      "Provide 1-3 candidate tools and a clarifying question.",
    input_schema: {
      type: "object" as const,
      properties: {
        candidates: {
          type: "array",
          items: {
            type: "object",
            properties: {
              tool: { type: "string" },
              reason: { type: "string" },
            },
            required: ["tool", "reason"],
          },
        },
        clarifying_question: { type: "string" },
      },
      required: ["candidates", "clarifying_question"],
    },
  },
  {
    name: "list_reseller_accounts",
    description: "List all reseller accounts. Intent: 'show my accounts', 'list resellers', 'what accounts do I have'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "get_reseller_info",
    description: "Get info about a specific reseller. Intent: 'reseller info', 'account details for reseller X'.",
    input_schema: { type: "object" as const, properties: { reseller_id: { type: "number" } } },
  },
  {
    name: "esim_status_per_account",
    description: "Show eSIM counts by status per account. Intent: 'esim overview', 'how many esims active per account'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "get_subscriber",
    description: "Look up a single subscriber by ICCID or MSISDN. Intent: 'get subscriber', 'look up ICCID', 'find SIM 8931...'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, msisdn: { type: "string" } } },
  },
  {
    name: "list_subscribers",
    description: "List subscribers in an account. Intent: 'list subscribers', 'show all SIMs', 'subscribers in account X'.",
    input_schema: { type: "object" as const, properties: { account_id: { type: "number" }, page: { type: "number" } } },
  },
  {
    name: "subscriber_usage",
    description: "Show data usage for a subscriber (max 7 days). Intent: 'how much data did X use', 'usage for subscriber', 'data consumption'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, start: { type: "string" }, end: { type: "string" } } },
  },
  {
    name: "subscriber_network_events",
    description: "Show network events (attach/detach/roaming) for a subscriber. Intent: 'network events', 'connection history', 'roaming events'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, start: { type: "string" }, end: { type: "string" } } },
  },
  {
    name: "list_subscriber_packages",
    description: "List active packages for a subscriber. Intent: 'what packages does subscriber have', 'show data packages', 'subscriber plan'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "list_package_templates",
    description: "List available package templates. Intent: 'what packages can I assign', 'show product catalog', 'available data plans'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "assign_package",
    description: "Assign a one-time data package to a subscriber. Intent: 'assign package', 'give subscriber X the Y plan', 'add data package'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, packageTemplateId: { type: "number" } } },
  },
  {
    name: "assign_recurring_package",
    description: "Set up auto-renewing recurring package for a subscriber. Intent: 'monthly plan', 'recurring package', 'auto-renew data'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, packageTemplateId: { type: "number" } } },
  },
  {
    name: "modify_package_limits",
    description: "Change data/voice/SMS limits on an existing package. Intent: 'change package limit', 'update data cap', 'modify plan limits'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, packageId: { type: "number" } } },
  },
  {
    name: "modify_package_expiry",
    description: "Change the expiry date of a subscriber package. Intent: 'extend package', 'change expiry', 'push package end date'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, packageId: { type: "number" }, expiryDate: { type: "string" } } },
  },
  {
    name: "modify_package_status",
    description: "Activate or pause a specific package. Intent: 'pause package', 'activate package', 'suspend data plan'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, packageId: { type: "number" }, status: { type: "string" } } },
  },
  {
    name: "stop_resume_recurring_package",
    description: "Stop or resume a recurring package. Intent: 'cancel auto-renew', 'stop recurring', 'resume monthly plan'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, packageId: { type: "number" }, action: { type: "string" } } },
  },
  {
    name: "delete_subscriber_package",
    description: "DESTRUCTIVE: Delete a package from a subscriber. Intent: 'delete package', 'remove plan from subscriber'. Requires confirm_token.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, packageId: { type: "number" } } },
  },
  {
    name: "clean_all_packages",
    description: "DESTRUCTIVE: Remove ALL packages from a subscriber. Irreversible. Intent: 'reset packages', 'clean all plans', 'wipe packages'. Requires confirm_token.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "modify_subscriber_status",
    description: "DESTRUCTIVE if terminating: Change subscriber status (ACTIVE/SUSPENDED/TERMINATED). Intent: 'suspend subscriber', 'pause SIM', 'terminate', 'reactivate'. Requires confirm_token.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, status: { type: "string" } } },
  },
  {
    name: "modify_subscriber_balance",
    description: "Add or set credit balance for a subscriber. Intent: 'top up subscriber', 'add credit', 'set subscriber balance'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, amount: { type: "number" } } },
  },
  {
    name: "modify_account_balance",
    description: "DESTRUCTIVE: Modify account-level balance. Intent: 'adjust account balance', 'add account credit'. Requires confirm_token.",
    input_schema: { type: "object" as const, properties: { accountId: { type: "number" }, amount: { type: "number" } } },
  },
  {
    name: "modify_subscriber_contact_info",
    description: "Update subscriber contact details. Intent: 'update subscriber name', 'change email for SIM', 'fix contact info'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, name: { type: "string" }, email: { type: "string" } } },
  },
  {
    name: "set_subscriber_traffic_restrictions",
    description: "Enable or disable voice/SMS/data restrictions. Intent: 'block data for subscriber', 'restrict voice', 'data-only SIM'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "modify_subscriber_steering_list",
    description: "Set preferred network steering list for a subscriber. Intent: 'change network preference', 'steer to operator X', 'preferred network'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, steeringListId: { type: "number" } } },
  },
  {
    name: "push_steering_to_subscriber",
    description: "Push network steering config to subscriber device. Intent: 'push steering', 'apply network config', 'force network update'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "move_subscriber_range_to_account",
    description: "Move a range of subscribers (by IMSI or ICCID) from one account to another. Intent: 'move subscribers', 'transfer SIMs to account'.",
    input_schema: { type: "object" as const, properties: { srcAccountId: { type: "number" }, destAccount: { type: "number" }, rangeType: { type: "string" } } },
  },
  {
    name: "hlr_get_bitrate",
    description: "Get current HLR bitrate cap for a subscriber. Intent: 'what speed is subscriber throttled to', 'get bitrate', 'check speed cap'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "hlr_set_bitrate",
    description: "Set or remove HLR speed cap (throttle). Intent: 'throttle subscriber', 'set speed limit', 'cap to 256kbps', 'remove throttle'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, bitrate: { type: "number" } } },
  },
  {
    name: "change_sim_status",
    description: "DESTRUCTIVE: Change physical SIM card status. Intent: 'deactivate SIM', 'delete SIM card', 'suspend card'. Requires confirm_token.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, status: { type: "string" } } },
  },
  {
    name: "send_sms",
    description: "Send SMS to a subscriber. Intent: 'send text to subscriber', 'SMS ICCID X', 'message subscriber'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, message: { type: "string" } } },
  },
  {
    name: "get_sim_provider_status",
    description: "Get SIM provider / eSIM profile status. Intent: 'SIM provider status', 'eSIM profile downloaded?', 'check profile status'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "get_subscriber_location",
    description: "Get approximate subscriber location. Intent: 'where is subscriber', 'subscriber location', 'locate SIM'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "get_subscriber_location_by_cell_id",
    description:
      "Get granular cell-tower location via GeoSense using radio_type, mcc, mnc, lac, and optional cell_id. " +
      "Use radio_type '4G' when the user means LTE — the literal 'LTE' is not valid for the underlying API. " +
      "NB-IoT aliases NBIOT, nbiot, nb-iot normalise to NB-IoT. " +
      "Intent: 'cell-level location', 'cell tower for subscriber', 'exact location', 'find subscriber by cell id', 'locate SIM by cell tower', 'NB-IoT subscriber location', 'which cell is this SIM on'.",
    input_schema: {
      type: "object" as const,
      required: ["radio_type", "mcc", "mnc", "lac"],
      properties: {
        iccid: { type: "string" },
        radio_type: {
          type: "string",
          description:
            "One of 2G, 3G, 4G, 5G, NB-IoT. Use 4G for LTE networks. NB-IoT also accepts NBIOT, nbiot, nb-iot.",
        },
        mcc: { type: "number", description: "Mobile Country Code" },
        mnc: { type: "number", description: "Mobile Network Code" },
        lac: { type: "number", description: "Location Area Code" },
        cell_id: { type: "number", description: "Cell tower ID (optional but recommended)" },
      },
    },
  },
  {
    name: "list_steering_lists",
    description: "List available network steering lists. Intent: 'show steering lists', 'available networks', 'what network profiles exist'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "list_network_profiles",
    description: "List network profiles. Intent: 'list network profiles', 'show network configs'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "list_location_zones",
    description: "List location zones. Intent: 'list zones', 'show coverage zones'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "list_detailed_location_zones",
    description: "List detailed location zones with coordinates. Intent: 'detailed zones', 'zone coordinates', 'coverage map'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "list_destination_prefixes",
    description: "List destination number prefixes. Intent: 'list prefixes', 'routing prefixes', 'number ranges'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "list_destination_lists",
    description: "List destination lists for voice/SMS routing. Intent: 'destination lists', 'routing lists'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "list_sponsors",
    description: "List sponsor accounts. Intent: 'list sponsors', 'show sponsor accounts'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "get_tariff",
    description: "Get tariff/rate rules. Intent: 'what are my rates', 'tariff info', 'pricing rules'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "create_package_template",
    description: "Create a new package template. Intent: 'create package', 'new product', 'add package template'.",
    input_schema: { type: "object" as const, properties: { name: { type: "string" } } },
  },
  {
    name: "create_location_zone",
    description: "Create a new location zone. Intent: 'create zone', 'add location zone', 'new coverage zone'.",
    input_schema: { type: "object" as const, properties: { name: { type: "string" } } },
  },
  {
    name: "modify_template_core",
    description: "Modify core settings of a package template. Intent: 'edit package template', 'change template name/settings'.",
    input_schema: { type: "object" as const, properties: { templateId: { type: "number" } } },
  },
  {
    name: "modify_template_recurring",
    description: "Modify recurring billing settings of a template. Intent: 'change template renewal', 'edit recurring settings'.",
    input_schema: { type: "object" as const, properties: { templateId: { type: "number" } } },
  },
  {
    name: "modify_template_throttling",
    description: "Modify throttling settings of a template. Intent: 'change template speed', 'edit throttle on template'.",
    input_schema: { type: "object" as const, properties: { templateId: { type: "number" } } },
  },
  {
    name: "subscriber_active_period",
    description: "Get or set the active period for a subscriber. Intent: 'active period', 'subscriber validity', 'SIM activation window'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "list_recent_ocs_events",
    description: "List recent OCS events for a subscriber (last 50, 24h window). Intent: 'recent events', 'event history', 'OCS log'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "rate_limit_status",
    description:
      "Inspect Bridge4IP OCS rate-limit governor state — bucket fill levels and reseller quota per endpoint. " +
      "Intent: 'are we rate limited', 'ocs quota', 'rate limit status', " +
      "'how full is the api bucket', 'check rate limit', 'api capacity'.",
    input_schema: {
      type: "object" as const,
      properties: { endpoint: { type: "string" } },
    },
  },
  {
    name: "subscriber_country_history",
    description:
      "Return cross-border movement history for a subscriber, reconstructed from OCS Gy charging events. " +
      "Intent: 'where has subscriber X traveled', 'country history for ICCID', " +
      "'border crossings', 'which countries did this SIM visit', 'travel history'.",
    input_schema: {
      type: "object" as const,
      properties: {
        subscriberId: { type: "string" },
        startDate: { type: "string" },
        endDate: { type: "string" },
        limit: { type: "number" },
      },
      required: ["subscriberId"],
    },
  },
  {
    name: "affect_subscriber_phone_number",
    description: "Assign or unassign a phone number to a subscriber. Intent: 'assign phone number', 'give SIM a number', 'remove number'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, msisdn: { type: "string" } } },
  },
  {
    name: "modify_subscriber_mobile_plan",
    description: "Change the mobile plan for a subscriber. Intent: 'change mobile plan', 'switch plan', 'update rate plan'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, planId: { type: "number" } } },
  },
  {
    name: "modify_subscriber_package_active_period",
    description: "Modify the active period of a subscriber's package. Intent: 'extend package period', 'change package dates'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" }, packageId: { type: "number" } } },
  },
  {
    name: "modify_subscriber_voip_plan",
    description: "Change VoIP plan for a subscriber. Intent: 'change voip plan', 'update voice plan'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "reset_subscriber_gz_counter",
    description: "DESTRUCTIVE: Reset a subscriber's guaranteed zone counter. Irreversible. Intent: 'reset gz counter', 'clear gz usage'. Requires confirm_token.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "diagnose_subscriber",
    description: "Run composite diagnostic on a subscriber. Intent: 'why is subscriber offline', 'diagnose SIM', 'troubleshoot ICCID'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "fleet_health",
    description: "Show overall fleet health (active/suspended/churned counts, alerts). Intent: 'fleet status', 'how is my fleet', 'health overview', 'how many esims active'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "detect_usage_anomalies",
    description: "Detect unusual data usage patterns across the fleet. Intent: 'usage anomalies', 'abnormal data use', 'spike detection'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "optimize_package",
    description: "Suggest package optimizations for a subscriber based on usage. Intent: 'optimize package', 'right-size plan', 'package recommendation'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "churn_risk",
    description: "Identify subscribers at risk of churning. Intent: 'churn risk', 'at-risk subscribers', 'who might leave'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "audit_network_coverage",
    description: "Audit network coverage for subscriber or fleet. Intent: 'coverage audit', 'network coverage check', 'coverage gaps'.",
    input_schema: { type: "object" as const, properties: { iccid: { type: "string" } } },
  },
  {
    name: "marketing_intelligence",
    description: "Get marketing intelligence: usage trends, popular packages, growth metrics. Intent: 'marketing data', 'growth report', 'popular plans'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "high_cost_subscribers",
    description: "List subscribers generating highest costs. Intent: 'high cost subscribers', 'most expensive SIMs', 'cost outliers'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "fleet_health_app",
    description: "Open the fleet health MCP App UI. Intent: 'open fleet dashboard app', 'fleet health UI'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "provision_esim_wizard",
    description: "Open the eSIM provisioning wizard app. Intent: 'provision esim', 'new esim wizard', 'onboard esim'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "balance_topup_form",
    description: "Open the balance top-up form app. Intent: 'top up balance', 'add credit form', 'balance topup'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "carrier_ask_describe",
    description: "Get documentation for a specific carrier tool. Intent: 'describe tool X', 'how does assign_package work', 'tool documentation'.",
    input_schema: { type: "object" as const, properties: { tool_name: { type: "string" } } },
  },
  {
    name: "greenzone_whitelist_add",
    description: "Add a host and/or IP to the GreenZone whitelist. Operates via portal-only Kapture browser automation — no direct REST API. Intent: 'add to greenzone whitelist', 'whitelist this host in greenzone', 'allow host in greenzone', 'greenzone add host', 'add IP to greenzone'.",
    input_schema: {
      type: "object" as const,
      properties: {
        host: { type: "string", description: "Hostname to whitelist (e.g. api.example.com)" },
        ip: { type: "string", description: "IP address to whitelist (optional)" },
      },
    },
  },
  {
    name: "greenzone_whitelist_remove",
    description: "Remove a host and/or IP from the GreenZone whitelist. Operates via portal-only Kapture browser automation — no direct REST API. Intent: 'remove from greenzone whitelist', 'de-whitelist host in greenzone', 'block host in greenzone', 'greenzone remove host'.",
    input_schema: {
      type: "object" as const,
      properties: {
        host: { type: "string", description: "Hostname to remove" },
        ip: { type: "string", description: "IP address to remove (optional)" },
      },
    },
  },
  {
    name: "greenzone_whitelist_list",
    description: "List all hosts and IPs in the GreenZone whitelist (reads from KV cache populated by last portal sync). Intent: 'show greenzone whitelist', 'list greenzone hosts', 'what hosts are in greenzone', 'greenzone whitelist', 'show greenzone entries', 'which IPs are whitelisted in greenzone'.",
    input_schema: { type: "object" as const, properties: {} },
  },
  {
    name: "subscriber_depletion_events",
    description:
      "Look up bundle depletion events from prepaid-usage webhooks (last_slice notifications, 7-day retention). " +
      "Intent: 'did subscriber X run out of data', 'depletion history for ICCID', 'bundle depleted events', " +
      "'which subscribers ran out of data', 'recent depletions', 'show data exhaustion events', " +
      "'has this SIM hit its data cap', 'list depleted subscribers'.",
    input_schema: {
      type: "object" as const,
      properties: {
        subscriberId: { type: "string", description: "OCS subscriber ID — omit to list fleet-wide depletions" },
        since: { type: "string", description: "ISO-8601 timestamp — filter to events at or after this time" },
      },
    },
  },
];

// System prompt for the Haiku router
/**
 * Built per call, not at module scope: descriptions are only recorded once the
 * tools have registered. Shared with apps/mcp-server so the two carrier_ask
 * copies cannot describe the same tool differently to the router.
 */
function routerTools(): BedrockTool[] {
  const [clarify, ...curated] = CURATED_ROUTER_TOOLS;
  return buildRouterCatalog({
    toolNames: TOOL_REGISTRY,
    curated: curated as unknown as RouterTool[],
    clarifyTool: clarify as unknown as RouterTool,
    getScope: (name: string) => ALL_TOOL_SCOPES[name] ?? "read",
  }) as unknown as BedrockTool[];
}

const ROUTER_SYSTEM_PROMPT = `You are a carrier fleet operations router. Your job is to map a user's natural-language intent to exactly ONE tool from the Carrier MCP tool registry.

${ROUTER_RULES}`;

// RouteResult variant from _routeIntent (confirm_token is "" placeholder; caller fills it)
type RoutedResult =
  | Extract<RouteResult, { match: "confirmed" }>
  | Extract<RouteResult, { match: "pending_confirm" }>
  | Extract<RouteResult, { match: "ambiguous" }>
  | Extract<RouteResult, { match: "none" }>
  | Extract<RouteResult, { match: "routing_pending" }>
  | Extract<RouteResult, { match: "rate_limited" }>;

// ---------------------------------------------------------------------------
// Transport lives in @carrier/ai.
//
// This file used to hand-roll its own AwsClient, URL and 429 handling — a third
// copy of a client the Worker had already consolidated twice. That drift is why
// this package shipped without per-org attribution or prompt caching long after
// both existed: apps/mcp-stdio is a near-verbatim fork of apps/mcp-server, so a
// fix applied to one silently misses the other. This is the PUBLISHED CLI, so
// the copy end users install was the unattributed one.
//
// Region and model id defaults now come from @carrier/ai too.
// ---------------------------------------------------------------------------
/**
 * Wall-clock ceiling for one routing call, shorter than @carrier/ai's 30s
 * default: a person is sitting on an MCP call waiting for a single routing
 * decision, and giving up at 12s beats holding the call open for half a minute.
 */
const ROUTER_TIMEOUT_MS = 12_000;

/**
 * Optional wiring for one routing call.
 *
 * An options bag rather than more positional parameters: both of these are
 * about observability, neither is required for routing to work, and a fifth
 * positional argument at a call site says nothing about which one it is.
 */
export interface RouteIntentOptions {
  /**
   * Puts the org id on the call in the Bedrock invocation log. Optional so the
   * exported helper stays testable without inventing a tenant; the production
   * caller always supplies it, because an unattributed call is spend nobody can
   * be charged for.
   */
  attribution?: AiAttribution;
  /**
   * Token counts, fired as soon as the model reports them. That is not the
   * same as "the route succeeded": a later step can still throw, and those
   * tokens were billed anyway, so a caller should record what it captured
   * rather than assuming an error means zero spend.
   */
  onUsage?: (usage: TokenUsage) => void;
}

/**
 * Raised when carrier_ask has no way to reach Bedrock.
 *
 * A distinct type rather than a bare Error so the tool handler can tell "the
 * router is switched off" from "the model call failed", and report the first as
 * a configuration problem the caller can fix instead of an outage they cannot.
 */
export class AskRouterNotConfiguredError extends Error {
  constructor(public readonly missing: string[]) {
    super(routerRemediation(missing));
    this.name = "AskRouterNotConfiguredError";
  }
}

/** Which of the router's inputs are absent, in the order a user should set them. */
function missingRouterCredentials(env: ToolContext["env"]): string[] {
  const missing: string[] = [];
  if (!env.AWS_ACCESS_KEY_ID) missing.push("AWS_ACCESS_KEY_ID");
  if (!env.AWS_SECRET_ACCESS_KEY) missing.push("AWS_SECRET_ACCESS_KEY");
  if (env.CARRIER_ASK_ENABLED !== "true") missing.push("CARRIER_ASK_ENABLED=true");
  return missing;
}

/**
 * Remediation a person running the published CLI can actually carry out.
 *
 * The old text said "add these to Doppler carrier/dev+stg+prd", which is an
 * instruction for whoever operates the hosted Worker. A CLI user has no Doppler
 * project and no way to get one, so the only honest instruction names the
 * process environment they control.
 */
function routerRemediation(missing: string[]): string {
  return (
    `carrier_ask cannot route: ${missing.join(", ")} ${
      missing.length === 1 ? "is" : "are"
    } not set. ` +
    "Set them in the environment of the process running this MCP server " +
    "(for Claude Desktop / Cursor / Windsurf, the `env` block of the server entry " +
    "in your MCP config), then restart it. The router calls Amazon Bedrock, so the " +
    "credentials must belong to an IAM identity with bedrock:InvokeModel. " +
    "Every other Carrier tool works without this — carrier_ask only picks which one to call, " +
    "so you can call the tool you want directly in the meantime."
  );
}

// ---------------------------------------------------------------------------
// _routeIntent — core routing logic via Claude Haiku 4.5 on Bedrock.
// Exported for testing (mock callBedrock or globalThis.fetch in tests).
// ---------------------------------------------------------------------------
export async function _routeIntent(
  intent: string,
  context: { iccid?: string; account_id?: number; reseller_id?: number },
  env: ToolContext["env"],
  opts: RouteIntentOptions = {},
): Promise<RoutedResult> {
  const { attribution, onUsage } = opts;
  // Credential gate. This THROWS rather than returning, and the difference is
  // the whole point.
  //
  // It used to return `{ match: "routing_pending", note: "...add these to
  // Doppler..." }` — a RoutedResult, so the tool wrapped it as a successful
  // call. `match` is the field a calling agent reads to decide what happened,
  // and "routing_pending" reads as "I looked and found nothing", so the agent
  // moved on and the user was never told the router was switched off. On the
  // CLI it was switched off for everyone: the env shim carried none of these
  // variables, so the gate could not open. A dead path that answers is worse
  // than a dead path that fails, because nobody goes looking for it.
  if (env.CARRIER_ASK_ENABLED !== "true" || !env.AWS_ACCESS_KEY_ID || !env.AWS_SECRET_ACCESS_KEY) {
    throw new AskRouterNotConfiguredError(missingRouterCredentials(env));
  }

  // Merge context into user message (omit keys with undefined — JSON.stringify drops them)
  const definedContext = Object.fromEntries(
    Object.entries(context).filter(([, value]) => value !== undefined),
  );
  const contextNote =
    Object.keys(definedContext).length > 0
      ? `\n\nPre-resolved context: ${JSON.stringify(definedContext)}`
      : "";
  const userMessage = `${intent}${contextNote}`;

  // Prompt caching on system prompt + tools, both static across calls. Marking
  // the last tool caches the whole array; @carrier/ai marks the system block
  // itself. A hit cuts input cost by roughly 90%. The Worker has done this for a
  // while and the fork had not, so CLI users were paying full price per route.
  const routerToolList = routerTools();
  const tools = routerToolList.map((tool, idx) =>
    idx === routerToolList.length - 1
      ? { ...tool, cache_control: { type: "ephemeral" as const } }
      : tool,
  );

  let toolCall: ToolCallResult | null;
  try {
    toolCall = await invokeTool(
      {
        accessKeyId: env.AWS_ACCESS_KEY_ID,
        secretAccessKey: env.AWS_SECRET_ACCESS_KEY,
        ...(env.AWS_SESSION_TOKEN ? { sessionToken: env.AWS_SESSION_TOKEN } : {}),
        ...(env.AWS_REGION ? { region: env.AWS_REGION } : {}),
        ...(env.BEDROCK_MODEL_ID ? { modelId: env.BEDROCK_MODEL_ID } : {}),
      },
      {
        system: ROUTER_SYSTEM_PROMPT,
        messages: [{ role: "user", content: userMessage }],
        maxTokens: 512,
        tools,
        timeoutMs: ROUTER_TIMEOUT_MS,
        ...(attribution ? { attribution } : {}),
        ...(onUsage ? { onUsage } : {}),
      },
    );
  } catch (err) {
    if (err instanceof BedrockRateLimitError) {
      const parsedSeconds = err.retryAfter ? parseInt(err.retryAfter, 10) : 60;
      return {
        match: "rate_limited",
        retry_after_seconds: Number.isFinite(parsedSeconds) ? parsedSeconds : 60,
        suggestion: "Bedrock rate limit reached. Please retry after the indicated delay.",
      };
    }
    throw err;
  }

  if (!toolCall) {
    return {
      match: "none",
      closest: [],
      suggestion:
        "Could not determine the right tool for this intent. Try rephrasing or use carrier_ask_describe to explore available tools.",
    };
  }

  const { name: resolvedTool, input } = toolCall;
  const resolvedParams = (input ?? {}) as Record<string, unknown>;

  // Clarification sentinel
  if (resolvedTool === "carrier_clarify") {
    const candidates = (resolvedParams.candidates ?? []) as Array<{ tool: string; reason: string }>;
    const clarifyingQuestion = typeof resolvedParams.clarifying_question === "string"
      ? resolvedParams.clarifying_question
      : "Could you clarify your intent?";
    return {
      match: "ambiguous",
      candidates,
      clarifying_question: clarifyingQuestion,
    };
  }

  // Hallucination guard
  if (!TOOL_REGISTRY.has(resolvedTool)) {
    return {
      match: "none",
      closest: [],
      suggestion: `Router returned unknown tool '${resolvedTool}'. Please try rephrasing your intent.`,
    };
  }

  // HARD_BLOCK: require confirm_token
  if (HARD_BLOCK_TOOLS.has(resolvedTool)) {
    return {
      match: "pending_confirm",
      resolved_tool: resolvedTool,
      resolved_params: resolvedParams,
      confidence: 0.95,
      confirm_required: true,
      confirm_token: "", // filled by caller after storeConfirmToken
      confirm_expires_in_seconds: CONFIRM_TOKEN_TTL_SECONDS,
      safety_note:
        `'${resolvedTool}' is a protected destructive operation. ` +
        "Re-call carrier_ask with the returned confirm_token to execute.",
    };
  }

  // Safe read/write tool
  return {
    match: "confirmed",
    resolved_tool: resolvedTool,
    resolved_params: resolvedParams,
    confidence: 0.95,
    confirm_required: false,
    execution_note:
      `Call '${resolvedTool}' directly with resolved_params to execute. ` +
      "carrier_ask does not auto-execute — the caller makes the explicit tool call.",
  };
}

// ---------------------------------------------------------------------------
// Audit log write for carrier_ask routing events.
// ---------------------------------------------------------------------------
function writeCarrierAskAudit(
  env: ToolContext["env"],
  row: {
    intent_hash: string;
    match: string;
    resolved_tool: string;
    confirm_token_state: "none" | "issued" | "redeemed" | "expired";
    status: "ok" | "error";
    latency_ms: number;
    sub: string;
    reseller_id: number;
    /** Which model answered. "none" when no model call was made. */
    model_id: string;
    tier: string;
    /** Zero on any path that did not reach the model. */
    usage: TokenUsage;
  },
): void {
  try {
    env.AUDIT_LOG.writeDataPoint({
      // The marker at blob[11] is what separates these rows from writeAudit's,
      // which writes a different layout to this same dataset — its blob[4] is
      // `sub` where this one's is an intent hash. Filter on it before reading
      // any other position.
      blobs: withSchemaMarker(
        [
          "carrier_ask",             // blob[0] tool_name
          row.resolved_tool,         // blob[1] resolved tool (or "none")
          row.status,                // blob[2] ok | error
          row.match,                 // blob[3] match type
          row.intent_hash,           // blob[4] SHA-256 of intent
          row.confirm_token_state,   // blob[5] confirm token state
          row.sub,                   // blob[6] user subject
          // Appended, never reordered: blob positions are the query contract and
          // an existing dashboard reading blob[3] must keep reading match.
          row.model_id,              // blob[7] model id, or "none"
          row.tier,                  // blob[8] tier at call time
        ],
        SCHEMA_CARRIER_ASK,
      ),
      // Same rule for doubles. latency stays at [0].
      doubles: [
        row.latency_ms,
        row.usage.inputTokens,
        row.usage.outputTokens,
        row.usage.cacheReadTokens,
        row.usage.cacheWriteTokens,
        // An estimate from a rate card we maintain, not a billed figure. It is
        // here so a per-tenant cost query is one SQL statement rather than a
        // join against a rate table that lives in another package.
        estimateCostUsd(row.usage),
      ],
      indexes: [String(row.reseller_id)],
    });
  } catch {
    // Never let audit failure propagate
  }
}

// ---------------------------------------------------------------------------
// registerAllCarrierAskTools — registers carrier_ask + carrier_ask_describe
// ---------------------------------------------------------------------------
export function registerAllCarrierAskTools(
  server: McpServer,
  ctx: ToolContext,
): void {
  // =========================================================================
  // carrier_ask — natural-language router
  // =========================================================================
  server.registerTool(
    "carrier_ask",
    {
      title: "Natural Language Carrier Tool Router",
      description:
        "Use this when you want to perform a carrier operation but don't know which specific tool to call. " +
        "Describe your intent in plain language and carrier_ask will identify the correct tool(s) and " +
        "suggest the required parameters. For simple read intents, it executes directly and returns results. " +
        "For destructive operations it returns a confirm_token that you must pass in a second call. " +
        "Params: `intent` (string — natural language description of what you want to do), " +
        "`context` (optional: iccid, account_id, reseller_id if already known). " +
        "Returns: RouteResult — confirmed | pending_confirm | ambiguous | none. " +
        "Do NOT use this when you know the right tool — direct calls are faster and cheaper.",
      inputSchema: {
        intent: z
          .string()
          .min(3)
          .describe(
            "Natural-language description of the operation to perform " +
            "(e.g. 'suspend ICCID 89316 until next month', 'show fleet health', " +
            "'assign Europe 5GB package to subscriber 89316...')",
          ),
        context: z
          .object({
            iccid: z.string().optional().describe("Subscriber ICCID if already known"),
            account_id: z.number().optional().describe("Account ID if already known"),
            reseller_id: z.number().optional().describe("Reseller ID if already known"),
          })
          .optional()
          .describe("Optional pre-resolved context to improve routing accuracy"),
        confirm_token: z
          .string()
          .optional()
          .describe(
            "One-time token from a previous carrier_ask call with confirm_required=true. " +
            "Providing this executes the previously staged destructive operation.",
          ),
      },
    },
    wrapHandler(
      "carrier_ask",
      "[carrier_ask]",
      "read",
      ctx,
      async (args, _token) => {
        const { intent, context = {}, confirm_token } = args;
        const intentHash = hashIntent(intent);
        const start = Date.now();

        // ------------------------------------------------------------------
        // Confirm token redemption path
        // ------------------------------------------------------------------
        if (confirm_token) {
          const staged = await consumeConfirmToken(ctx.env.OAUTH_KV, confirm_token);
          if (!staged) {
            writeCarrierAskAudit(ctx.env, {
              intent_hash: intentHash,
              match: "confirm_expired",
              resolved_tool: "none",
              confirm_token_state: "expired",
              status: "error",
              latency_ms: Date.now() - start,
              sub: ctx.props.sub,
              reseller_id: ctx.props.reseller_id,
              model_id: "none",
              tier: ctx.props.tier,
              usage: ZERO_USAGE,
            });
            return {
              content: [
                {
                  type: "text" as const,
                  text: JSON.stringify({
                    error: "confirm_token_expired",
                    message:
                      `Confirm token '${confirm_token}' has expired or was already used. ` +
                      `Tokens expire after ${CONFIRM_TOKEN_TTL_SECONDS}s. Re-issue the original intent to get a new token.`,
                  }),
                },
              ],
              isError: true,
            };
          }

          const resolvedTool = staged.resolved_tool as string;
          if (!TOOL_REGISTRY.has(resolvedTool)) {
            return {
              content: [
                {
                  type: "text" as const,
                  text: JSON.stringify({
                    error: "invalid_tool",
                    message: `Staged tool '${resolvedTool}' is not in the tool registry.`,
                  }),
                },
              ],
              isError: true,
            };
          }

          writeCarrierAskAudit(ctx.env, {
            intent_hash:
              typeof staged.intent_hash === "string" ? staged.intent_hash : intentHash,
            match: "confirm_executed",
            resolved_tool: resolvedTool,
            confirm_token_state: "redeemed",
            status: "ok",
            latency_ms: Date.now() - start,
            sub: ctx.props.sub,
            reseller_id: ctx.props.reseller_id,
            model_id: "none",
            tier: ctx.props.tier,
            usage: ZERO_USAGE,
          });

          return {
            content: [
              {
                type: "text" as const,
                text: JSON.stringify({
                  match: "confirm_executed",
                  resolved_tool: resolvedTool,
                  resolved_params: staged.resolved_params,
                  note:
                    "Confirm token accepted. Call the resolved_tool directly with resolved_params " +
                    "to execute the operation. carrier_ask does not auto-execute — the caller must " +
                    "make the explicit tool call.",
                }),
              },
            ],
          };
        }

        // ------------------------------------------------------------------
        // Main routing path
        // ------------------------------------------------------------------
        let route: RoutedResult;
        // Captured by callback rather than returned, so a routing path that
        // never touches Bedrock (feature flag off) is distinguishable from one
        // that did and used nothing.
        //
        // The flag is separate on purpose. extractUsage() returns the
        // ZERO_USAGE constant BY REFERENCE when a response carries no usage
        // field, so `routeUsage === ZERO_USAGE` cannot tell "never called" from
        // "called, reported nothing" — and getting that backwards stamps a real
        // billed call as model_id "none".
        let routeUsage: TokenUsage = ZERO_USAGE;
        let modelRan = false;

        try {
          route = await _routeIntent(intent, context, ctx.env, {
            attribution: {
              ...(ctx.props.org_id ? { orgId: ctx.props.org_id } : {}),
              ...(ctx.props.reseller_id
                ? { subAccountId: String(ctx.props.reseller_id) }
                : {}),
              surface: "carrier_ask",
              tier: ctx.props.tier,
            },
            onUsage: (u) => {
              routeUsage = u;
              modelRan = true;
            },
          });
        } catch (err) {
          // The router being switched off is a configuration state, not an
          // outage, and it must never reach the caller shaped like an answer.
          // No `match` field: that is the field an agent reads as a routing
          // verdict, and the whole failure this replaces was an agent reading
          // `match: "routing_pending"` as "nothing matched".
          if (err instanceof AskRouterNotConfiguredError) {
            writeCarrierAskAudit(ctx.env, {
              intent_hash: intentHash,
              match: "error",
              resolved_tool: "none",
              confirm_token_state: "none",
              status: "error",
              latency_ms: Date.now() - start,
              sub: ctx.props.sub,
              reseller_id: ctx.props.reseller_id,
              model_id: "none",
              tier: ctx.props.tier,
              usage: ZERO_USAGE,
            });
            return {
              content: [
                {
                  type: "text" as const,
                  text: JSON.stringify({
                    error: "router_not_configured",
                    missing: err.missing,
                    message: err.message,
                  }),
                },
              ],
              isError: true,
            };
          }
          writeCarrierAskAudit(ctx.env, {
            intent_hash: intentHash,
            match: "error",
            resolved_tool: "none",
            confirm_token_state: "none",
            status: "error",
            latency_ms: Date.now() - start,
            sub: ctx.props.sub,
            reseller_id: ctx.props.reseller_id,
            model_id: modelRan ? (ctx.env.BEDROCK_MODEL_ID ?? DEFAULT_MODEL_ID) : "none",
            tier: ctx.props.tier,
            // Not ZERO_USAGE: a call can throw AFTER the model answered, and
            // those tokens were billed regardless. Recording zero here is how
            // measured spend drifts below real spend in exactly the rows used
            // to check it.
            usage: routeUsage,
          });
          return {
            content: [
              {
                type: "text" as const,
                text: JSON.stringify({
                  error: "routing_error",
                  message:
                    err instanceof Error
                      ? err.message
                      : "An unexpected error occurred during routing.",
                }),
              },
            ],
            isError: true,
          };
        }

        // Issue confirm token for HARD_BLOCK results
        let finalRoute: RouteResult = route as RouteResult;
        let confirmTokenState: "none" | "issued" = "none";

        if (route.match === "pending_confirm") {
          const token = generateConfirmToken();
          await storeConfirmToken(ctx.env.OAUTH_KV, token, {
            resolved_tool: route.resolved_tool,
            resolved_params: route.resolved_params,
            intent_hash: intentHash,
            issued_at: new Date().toISOString(),
          });
          finalRoute = { ...route, confirm_token: token } as RouteResult;
          confirmTokenState = "issued";
        }

        const resolvedToolForAudit: string =
          (route.match === "confirmed" || route.match === "pending_confirm")
            ? route.resolved_tool
            : "none";

        writeCarrierAskAudit(ctx.env, {
          intent_hash: intentHash,
          match: route.match,
          resolved_tool: resolvedToolForAudit,
          confirm_token_state: confirmTokenState,
          status: "ok",
          latency_ms: Date.now() - start,
          sub: ctx.props.sub,
          reseller_id: ctx.props.reseller_id,
          model_id: modelRan ? (ctx.env.BEDROCK_MODEL_ID ?? DEFAULT_MODEL_ID) : "none",
          tier: ctx.props.tier,
          usage: routeUsage,
        });

        return {
          content: [
            {
              type: "text" as const,
              text: JSON.stringify(finalRoute, null, 2),
            },
          ],
        };
      },
    ),
  );

  // =========================================================================
  // carrier_ask_describe — drill-down companion
  // =========================================================================
  server.registerTool(
    "carrier_ask_describe",
    {
      title: "Describe a Carrier Tool",
      description:
        "Get full documentation for any registered Carrier MCP tool: description, " +
        "parameters, 2-3 example invocations, required scope, destructive flag, and guidance. " +
        "Params: `tool_name` (exact MCP tool name, e.g. 'assign_package'). " +
        "Returns: structured tool metadata. Does NOT execute the tool.",
      inputSchema: {
        tool_name: z
          .string()
          .describe("The exact MCP tool name to describe (e.g. 'assign_package', 'hlr_set_bitrate')"),
      },
    },
    wrapHandler(
      "carrier_ask_describe",
      "[carrier_ask_describe]",
      "read",
      ctx,
      async ({ tool_name }, _token) => {
        if (!TOOL_REGISTRY.has(tool_name)) {
          const closest = [...TOOL_REGISTRY]
            .filter((name) => name.includes(tool_name.split("_")[0] ?? "") || tool_name.includes(name.split("_")[0] ?? ""))
            .slice(0, 5);
          return {
            content: [
              {
                type: "text" as const,
                text: JSON.stringify({
                  error: "tool_not_found",
                  tool_name,
                  message: `'${tool_name}' is not a registered Carrier MCP tool.`,
                  did_you_mean: closest.length > 0 ? closest : undefined,
                  total_registered: TOOL_REGISTRY.size,
                }),
              },
            ],
            isError: true,
          };
        }

        const allScopes = { ...TOOL_SCOPES, ...BACKLOG_TOOL_SCOPES };
        const scope = allScopes[tool_name as keyof typeof allScopes] ?? "read";
        const isDestructive = DESTRUCTIVE_TOOLS.has(tool_name);
        const isHardBlock = HARD_BLOCK_TOOLS.has(tool_name);

        const doc = {
          tool_name,
          scope,
          destructive: isDestructive,
          hard_block: isHardBlock,
          hard_block_note: isHardBlock
            ? "This tool is in the HARD_BLOCK list: carrier_ask will never auto-execute it. " +
              "It always requires an explicit confirm_token redemption."
            : undefined,
          dry_run_supported: isDestructive,
          examples: buildExamples(tool_name),
          note:
            "Full parameter descriptions are available in the tool's inputSchema. " +
            "Call the tool with no arguments to trigger the MCP schema introspection response.",
        };

        return {
          content: [
            {
              type: "text" as const,
              text: JSON.stringify(doc, null, 2),
            },
          ],
        };
      },
    ),
  );
}

// ---------------------------------------------------------------------------
// buildExamples — curated invocation examples for high-traffic tools.
// ---------------------------------------------------------------------------
function buildExamples(toolName: string): Array<{ intent: string; params: Record<string, unknown> }> {
  const examples: Record<string, Array<{ intent: string; params: Record<string, unknown> }>> = {
    assign_package: [
      { intent: "Give subscriber 89316... the Europe 5GB package", params: { iccid: "89316...", packageTemplateId: 42 } },
      { intent: "Assign a one-time data package to this eSIM", params: { iccid: "89316...", packageTemplateId: 42 } },
    ],
    assign_recurring_package: [
      { intent: "Set up monthly auto-renewal for subscriber 89316...", params: { iccid: "89316...", packageTemplateId: 55 } },
    ],
    modify_subscriber_status: [
      { intent: "Pause Maya's subscription for 2 months", params: { iccid: "89316...", status: "SUSPENDED" } },
      { intent: "Reactivate subscriber 89316...", params: { iccid: "89316...", status: "ACTIVE" } },
    ],
    hlr_set_bitrate: [
      { intent: "Throttle ICCID 89316... to 256kbps", params: { iccid: "89316...", bitrate: 256000 } },
      { intent: "Remove speed cap from subscriber 89316...", params: { iccid: "89316...", bitrate: 0 } },
    ],
    modify_subscriber_steering_list: [
      { intent: "Connect this eSIM to the best network in Italy", params: { iccid: "89316...", steeringListId: 7 } },
    ],
    push_steering_to_subscriber: [
      { intent: "Push the new steering config to the device", params: { iccid: "89316..." } },
    ],
    clean_all_packages: [
      { intent: "Reset all packages for subscriber 89316... before reprovisioning", params: { iccid: "89316...", dry_run: true } },
    ],
    fleet_health: [
      { intent: "Show me the fleet status overview", params: {} },
      { intent: "How many eSIMs are active right now", params: {} },
    ],
    diagnose_subscriber: [
      { intent: "Why is subscriber 89316... offline?", params: { iccid: "89316..." } },
    ],
  };

  return examples[toolName] ?? [
    { intent: `Call ${toolName} for subscriber 89316...`, params: { iccid: "89316..." } },
  ];
}

// Export for TOOL_INVENTORY generation and reconcile script
export { TOOL_REGISTRY, HARD_BLOCK_TOOLS };
